import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle, XCircle, IndianRupee, TrendingUp, Award, Plus, Pencil, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  eligible: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-muted text-muted-foreground',
};

export default function IncentivesPage() {
  const [incentives, setIncentives] = useState([]);
  const [slabs, setSlabs] = useState([]);
  const [pendingForPayroll, setPendingForPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slabDialogOpen, setSlabDialogOpen] = useState(false);
  const [editSlab, setEditSlab] = useState(null);
  const [slabForm, setSlabForm] = useState({ min_revenue: '', max_revenue: '', incentive_percentage: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [incRes, slabRes, pendingRes] = await Promise.all([
        api.get('/finance/incentives'),
        api.get('/finance/incentives/slabs'),
        api.get('/finance/incentives/pending-for-payroll'),
      ]);
      setIncentives(incRes.data);
      setSlabs(slabRes.data);
      setPendingForPayroll(pendingRes.data);
    } catch {
      toast.error('Failed to load incentives data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;

  const totalEligible = incentives.filter(i => i.status === 'eligible').reduce((s, i) => s + i.incentive_amount, 0);
  const totalApproved = incentives.filter(i => i.status === 'approved').reduce((s, i) => s + i.incentive_amount, 0);
  const totalPaid = incentives.filter(i => i.status === 'paid').reduce((s, i) => s + i.incentive_amount, 0);

  const approveIncentive = async (id) => {
    try {
      await api.put(`/finance/incentives/${id}/approve`);
      toast.success('Incentive approved for payroll');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const cancelIncentive = async (id) => {
    if (!window.confirm('Cancel this incentive?')) return;
    try {
      await api.put(`/finance/incentives/${id}/cancel`);
      toast.success('Incentive cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const openAddSlab = () => {
    setSlabForm({ min_revenue: '', max_revenue: '', incentive_percentage: '', description: '' });
    setEditSlab(null);
    setSlabDialogOpen(true);
  };

  const openEditSlab = (slab) => {
    setSlabForm({
      min_revenue: slab.min_revenue?.toString() || '',
      max_revenue: slab.max_revenue?.toString() || '',
      incentive_percentage: slab.incentive_percentage?.toString() || '',
      description: slab.description || '',
    });
    setEditSlab(slab);
    setSlabDialogOpen(true);
  };

  const handleSaveSlab = async () => {
    if (!slabForm.min_revenue || !slabForm.incentive_percentage) {
      toast.error('Min revenue and incentive % are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        min_revenue: parseFloat(slabForm.min_revenue),
        max_revenue: slabForm.max_revenue ? parseFloat(slabForm.max_revenue) : null,
        incentive_percentage: parseFloat(slabForm.incentive_percentage),
        description: slabForm.description || null,
      };
      if (editSlab) {
        await api.put(`/finance/incentives/slabs/${editSlab.id}`, payload);
        toast.success('Slab updated');
      } else {
        await api.post('/finance/incentives/slabs', payload);
        toast.success('Slab created');
      }
      setSlabDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save slab');
    } finally {
      setSaving(false);
    }
  };

  const deleteSlab = async (id) => {
    if (!window.confirm('Delete this incentive slab?')) return;
    try {
      await api.delete(`/finance/incentives/slabs/${id}`);
      toast.success('Slab deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const setSlabField = (key, val) => setSlabForm(p => ({ ...p, [key]: val }));

  return (
    <div data-testid="incentives-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Incentives</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage recruiter incentives and slabs</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-border border-l-4 border-l-blue-500">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">Eligible</span>
          </div>
          <p className="text-xl font-heading font-bold text-blue-600" data-testid="total-eligible">{formatCurrency(totalEligible)}</p>
        </Card>
        <Card className="p-4 border-border border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">Approved</span>
          </div>
          <p className="text-xl font-heading font-bold text-emerald-600" data-testid="total-approved">{formatCurrency(totalApproved)}</p>
        </Card>
        <Card className="p-4 border-border border-l-4 border-l-violet-500">
          <div className="flex items-center gap-2 mb-1">
            <IndianRupee className="w-4 h-4 text-violet-600" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">Paid</span>
          </div>
          <p className="text-xl font-heading font-bold text-violet-600" data-testid="total-paid">{formatCurrency(totalPaid)}</p>
        </Card>
      </div>

      <Tabs defaultValue="records" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="records" data-testid="tab-records">Incentive Records</TabsTrigger>
          <TabsTrigger value="slabs" data-testid="tab-slabs">Incentive Slabs</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending for Payroll</TabsTrigger>
        </TabsList>

        {/* Incentive Records Tab */}
        <TabsContent value="records">
          <div className="border border-border bg-card rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Recruiter</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Invoice Value</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Incentive %</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Incentive Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Total Revenue</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : incentives.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No incentive records yet. Incentives are auto-generated when invoices are fully paid.</TableCell></TableRow>
                ) : incentives.map(inc => (
                  <TableRow key={inc.id} data-testid={`incentive-row-${inc.id}`}>
                    <TableCell className="font-medium">{inc.recruiter_name}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(inc.invoice_value)}</TableCell>
                    <TableCell className="text-right font-mono">{inc.incentive_percentage}%</TableCell>
                    <TableCell className="text-right font-mono font-medium text-primary">{formatCurrency(inc.incentive_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(inc.total_revenue)}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[inc.status]}>{inc.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {inc.status === 'eligible' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => approveIncentive(inc.id)} className="text-emerald-600" data-testid={`approve-incentive-${inc.id}`}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => cancelIncentive(inc.id)} className="text-red-600" data-testid={`cancel-incentive-${inc.id}`}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Slabs Tab */}
        <TabsContent value="slabs">
          <div className="flex justify-end mb-4">
            <Button onClick={openAddSlab} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="add-slab-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Slab
            </Button>
          </div>
          <div className="border border-border bg-card rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Min Revenue</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Max Revenue</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Incentive %</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slabs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No incentive slabs configured. Add slabs to enable automatic incentive calculation.</TableCell></TableRow>
                ) : slabs.map(slab => (
                  <TableRow key={slab.id} data-testid={`slab-row-${slab.id}`}>
                    <TableCell className="text-right font-mono">{formatCurrency(slab.min_revenue)}</TableCell>
                    <TableCell className="text-right font-mono">{slab.max_revenue ? formatCurrency(slab.max_revenue) : 'No limit'}</TableCell>
                    <TableCell className="text-right font-mono font-medium text-primary">{slab.incentive_percentage}%</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{slab.description || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditSlab(slab)} data-testid={`edit-slab-${slab.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteSlab(slab.id)} className="text-red-600" data-testid={`delete-slab-${slab.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Pending for Payroll Tab */}
        <TabsContent value="pending">
          <div className="border border-border bg-card rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Recruiter</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Total Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Incentive Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingForPayroll.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No approved incentives pending for payroll</TableCell></TableRow>
                ) : pendingForPayroll.map(item => (
                  <TableRow key={item.recruiter_id} data-testid={`pending-row-${item.recruiter_id}`}>
                    <TableCell className="font-medium">{item.recruiter_name}</TableCell>
                    <TableCell className="text-right font-mono font-medium text-emerald-600">{formatCurrency(item.total_amount)}</TableCell>
                    <TableCell className="text-right">{item.incentive_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Slab Dialog */}
      <Dialog open={slabDialogOpen} onOpenChange={setSlabDialogOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editSlab ? 'Edit Slab' : 'Add Incentive Slab'}</DialogTitle>
            <DialogDescription>Define revenue-based incentive percentage tiers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Min Revenue *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={slabForm.min_revenue} onChange={e => setSlabField('min_revenue', e.target.value)} className="pl-10 rounded-sm" placeholder="0" data-testid="input-min-revenue" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Max Revenue</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" value={slabForm.max_revenue} onChange={e => setSlabField('max_revenue', e.target.value)} className="pl-10 rounded-sm" placeholder="No limit" data-testid="input-max-revenue" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Incentive Percentage *</Label>
              <div className="relative">
                <TrendingUp className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="number" value={slabForm.incentive_percentage} onChange={e => setSlabField('incentive_percentage', e.target.value)} className="pl-10 rounded-sm" placeholder="5" data-testid="input-incentive-pct" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
              <Input value={slabForm.description} onChange={e => setSlabField('description', e.target.value)} className="rounded-sm" placeholder="e.g., Junior recruiter tier" data-testid="input-slab-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlabDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveSlab} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="save-slab-btn">
              {saving ? 'Saving...' : editSlab ? 'Update' : 'Create Slab'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
